import React, { FC, useState, useEffect, useRef } from "react";
import { logEvent, getAnalytics, Analytics } from "firebase/analytics";
import app from "../firebase";
import { useLocation } from "@reach/router";
import { Box, Stack } from "@mui/system";
import { Button, Typography, Divider, NoSsr, useTheme } from "@mui/material";
import { Link } from "gatsby";
import { useLocalStorage } from "usehooks-ts";
import CookieSettingsDialog from "./CookieSettingsDialog";
import {
  ALLOW_ANALYTICS_KEY,
  ALLOW_STRICTLY_NECESSARY_KEY,
} from "../constants";

const PrivacyPolicy: FC = () => {
  const [allowStrictlyNecessary, setAllowStrictlyNecessary] = useLocalStorage<
    boolean | undefined
  >(ALLOW_STRICTLY_NECESSARY_KEY, undefined);
  const [allowAnalytics, setAllowAnalytics] = useLocalStorage<
    boolean | undefined
  >(ALLOW_ANALYTICS_KEY, undefined);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const { pathname } = useLocation();
  const analytics = useRef<Analytics | null>(null);
  const theme = useTheme();

  const handleAccept = () => {
    setAllowStrictlyNecessary(true);
    setAllowAnalytics(true);
  };

  useEffect(() => {
    if (allowAnalytics) {
      analytics.current = getAnalytics(app);
    }
  }, [allowAnalytics]);

  useEffect(() => {
    if (analytics.current) {
      logEvent(analytics.current, "page_view", {
        page_path: pathname,
      });
    }
  }, [pathname]);

  return (
    <NoSsr>
      <CookieSettingsDialog
        open={showCookieSettings}
        onClose={() => setShowCookieSettings(false)}
      />
      <Stack
        sx={{
          position: "fixed",
          bottom: 0,
          zIndex: 1000,
          backgroundColor: "white",
          width: "100vw",
        }}
        display={
          allowStrictlyNecessary === undefined && allowAnalytics === undefined
            ? "flex"
            : "none"
        }
      >
        <Divider />
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          flex={1}
          sx={{
            maxWidth: theme.customSpacing?.containerMaxWidth,
            px: theme.customSpacing?.marginSide,
            pb: 2,
            pt: 3,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              width: {
                xs: "100%",
                md: "50%",
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                mb: 0,
                flex: 1,
                "& a": {
                  textDecoration: "underline",
                },
              }}
              color="text.secondary"
            >
              By clicking “Allow Cookies”, you agree to the storing of cookies
              on your device to enhance site navigation, analyze site usage, and
              assist in our marketing efforts. Described fully in our
              <>&nbsp;</>
              <Link to="/privacy-policy">Privacy Policy.</Link>
            </Typography>
          </Box>
          <Stack
            spacing={1}
            justifyContent={{ xs: "center", sm: "flex-end", md: "strech" }}
            alignItems={{ xs: "stretch", sm: "center", md: "flex-end" }}
            direction={{ xs: "column", sm: "row" }}
            sx={{
              width: { xs: "100%", sm: "auto" },
              "& button": {
                textTransform: "none",
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              },
            }}
            mt={{ xs: 3, md: 0 }}
          >
            <Button
              variant="outlined"
              onClick={() => setShowCookieSettings(true)}
              disableElevation
              sx={{
                alignItems: "center",
              }}
            >
              Cookie Settings
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAccept}
              disableElevation
              sx={{
                alignItems: "center",
              }}
            >
              Allow Cookies
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </NoSsr>
  );
};

export default PrivacyPolicy;
